// Outsourced-vendor charge capture — GET vendors + POST charge at dispatch time.
import axios from "axios";
import { getAuthHeaders } from "../../ipd-billing/constants";

const apiRoot = () => process.env.REACT_APP_BACKEND_ROUTE_URL;

/** medical_service_id from get_patient_lab_tests/ — skip blank / snomed placeholders. */
export function validMedicalServiceId(order) {
  const raw = order?.medicalServiceId ?? order?.medical_service_id;
  if (raw == null || raw === "" || raw === "-") return null;
  const s = String(raw).trim();
  if (/^snomed-/i.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && String(n) === s ? n : s;
}

/** IPD sample may be routed to a vendor when the patient has an admission id. */
export function canShowVendorFlow(orders, patient) {
  if (patient?.admissionId == null || patient.admissionId === "") return false;
  return (orders || []).some((o) => validMedicalServiceId(o) != null);
}

export async function fetchVendorChargeOptions(admissionId, medicalServiceId) {
  const res = await axios.get(
    `${apiRoot()}ipd_admission/${admissionId}/billing/vendor-charge-capture/`,
    {
      headers: getAuthHeaders(true),
      params: { medical_service_id: medicalServiceId },
      skipGlobalLoader: true,
    },
  );
  if (res.data?.status === "Failed") {
    throw new Error(res.data?.message || "Could not load vendor options");
  }
  return res.data;
}

export async function captureVendorCharge(admissionId, payload) {
  const res = await axios.post(
    `${apiRoot()}ipd_admission/${admissionId}/billing/vendor-charge-capture/`,
    payload,
    { headers: getAuthHeaders(true) },
  );
  if (res.data?.status === "Failed") {
    throw new Error(res.data?.message || "Vendor charge capture failed");
  }
  return res.data;
}

/**
 * Vendors that can run every outsourceable test on the sample, with rates summed.
 * Tests whose vendor list is empty are in-house only and do not constrain the pick.
 */
export async function fetchCommonVendorsForOrders(admissionId, orders) {
  const scoped = (orders || [])
    .map((order) => ({ order, medicalServiceId: validMedicalServiceId(order) }))
    .filter((row) => row.medicalServiceId != null);

  if (scoped.length === 0) {
    return { vendors: [], tests: [], canOutsource: false };
  }

  const tests = await Promise.all(
    scoped.map(async ({ order, medicalServiceId }) => {
      const data = await fetchVendorChargeOptions(admissionId, medicalServiceId);
      return {
        order,
        medicalServiceId,
        serviceName: data.service_name || order.service,
        vendors: Array.isArray(data.vendors) ? data.vendors : [],
      };
    }),
  );

  const outsourceable = tests.filter((t) => t.vendors.length > 0);
  if (outsourceable.length === 0) {
    return { vendors: [], tests, canOutsource: false };
  }

  const [first, ...rest] = outsourceable;
  const common = first.vendors.filter((v) =>
    rest.every((t) => t.vendors.some((row) => row.vendor_id === v.vendor_id)),
  );

  const vendors = common.map((v, index) => {
    const picked = outsourceable.map(
      (t) => t.vendors.find((row) => row.vendor_id === v.vendor_id) || {},
    );
    const vendorRate = picked.reduce(
      (sum, row) => sum + (Number(row.rate) || 0),
      0,
    );
    return {
      vendor_id: v.vendor_id,
      vendor_name: v.vendor_name,
      address: v.address || "",
      vendor_rate: vendorRate,
      is_default: index === 0,
      test_count: outsourceable.length,
    };
  });

  return { vendors, tests, canOutsource: vendors.length > 0 };
}

/** POST one or many vendor charges; returns charge_event.id keyed by orderNo. */
export async function captureVendorChargesForOrders(
  admissionId,
  orders,
  vendorId,
  { dispatchRef = "" } = {},
) {
  const chargeDate = new Date().toISOString().split("T")[0];
  const remarks = String(dispatchRef || "").trim() || undefined;
  const rows = (orders || [])
    .map((order) => ({
      order,
      medical_service_id: validMedicalServiceId(order),
    }))
    .filter((row) => row.medical_service_id != null);

  if (rows.length === 0) {
    return { chargeEventsByOrder: {}, captured: 0 };
  }

  const chargeEventsByOrder = {};

  if (rows.length === 1) {
    const { order, medical_service_id } = rows[0];
    const result = await captureVendorCharge(admissionId, {
      vendor_id: vendorId,
      medical_service_id,
      quantity: 1,
      charge_date: chargeDate,
      remarks,
    });
    const eventId = result?.charge_event?.id;
    if (eventId) chargeEventsByOrder[order.orderNo] = eventId;
    return { chargeEventsByOrder, captured: eventId ? 1 : 0 };
  }

  const payload = {
    charges: rows.map(({ medical_service_id }) => ({
      vendor_id: vendorId,
      medical_service_id,
      quantity: 1,
      charge_date: chargeDate,
      remarks,
    })),
  };
  const bulk = await captureVendorCharge(admissionId, payload);
  const results = Array.isArray(bulk?.results) ? bulk.results : [];
  results.forEach((entry, index) => {
    const order = rows[index]?.order;
    if (!order || !entry?.ok) return;
    const eventId = entry?.result?.charge_event?.id;
    if (eventId) chargeEventsByOrder[order.orderNo] = eventId;
  });
  const captured = Object.keys(chargeEventsByOrder).length;
  return { chargeEventsByOrder, captured, bulk };
}
